import { useQuery } from '@tanstack/react-query';
import { SAP_CONTRACT_TYPES, type SapEmploymentValues, type SapReferenceEntry } from '@hcc/types';
import { api } from '../../lib/api';
import { SearchableSelect, type SearchableOption } from '../ui/SearchableSelect';

/**
 * The five SAP employment-record inputs (org centre, job id, contract
 * type, weekly hours, working time model), shared by the per-person
 * override card on the SAP tab and the role-defaults editor. Pickers
 * are fed by the cached Step 0 reference data (refresh on /admin/sap).
 */

export function useSapReference(kind: string) {
  return useQuery({
    queryKey: ['sap-reference', kind],
    queryFn: () => api<{ entries: SapReferenceEntry[] }>(`/api/admin/sap/reference?kind=${kind}`),
    staleTime: 5 * 60 * 1000,
  });
}

const labelCls = 'text-xs font-medium text-fg-muted';
const inputCls =
  'mt-1 w-full rounded-token-sm border border-line/20 bg-surface px-2 py-1.5 text-sm';

export function EmploymentFieldsGrid({
  draft,
  onChange,
  hintFor,
}: {
  draft: SapEmploymentValues;
  onChange: (next: SapEmploymentValues) => void;
  /** Optional per-field annotation, e.g. "from role default". */
  hintFor?: (key: keyof SapEmploymentValues) => string | null;
}) {
  const orgCentres = useSapReference('org_centre');
  const models = useSapReference('working_time_model');
  const jobs = useSapReference('job');
  const jobEntries = jobs.data?.entries ?? [];
  const jobOptions: SearchableOption[] = jobEntries.map((e) => ({
    value: e.code,
    label: e.label,
    description: e.code,
  }));
  // A previously saved ID that fell out of the refreshed list stays selectable.
  if (draft.job_id && !jobEntries.some((e) => e.code === draft.job_id)) {
    jobOptions.unshift({ value: draft.job_id, label: `${draft.job_id} — not in cached job list` });
  }
  const hint = (key: keyof SapEmploymentValues) => {
    const text = hintFor?.(key);
    return text ? <span className="font-normal text-fg-dim"> · {text}</span> : null;
  };

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <label className="block">
        <span className={labelCls}>Organisational centre {hint('org_centre_id')}</span>
        <select
          className={inputCls}
          value={draft.org_centre_id ?? ''}
          onChange={(e) => onChange({ ...draft, org_centre_id: e.target.value || undefined })}
        >
          <option value="">Choose…</option>
          {(orgCentres.data?.entries ?? []).map((e) => (
            <option key={e.code} value={e.code}>
              {e.code} — {e.label}
            </option>
          ))}
        </select>
      </label>
      <label className="block">
        <span className={labelCls}>SAP job ID {hint('job_id')}</span>
        {/* Searchable dropdown once the Step 0.4 job query has been cached
            (hundreds of jobs); free-text fallback so an empty cache never
            blocks entering a known ID. */}
        {jobEntries.length > 0 ? (
          <div className="mt-1">
            <SearchableSelect
              options={jobOptions}
              value={draft.job_id ?? null}
              onChange={(v) => onChange({ ...draft, job_id: v ?? undefined })}
              searchPlaceholder="Search job name or ID…"
              ariaLabel="SAP job ID"
            />
          </div>
        ) : (
          <input
            className={inputCls}
            value={draft.job_id ?? ''}
            placeholder="Free text — refresh the job list on the SAP admin page"
            onChange={(e) => onChange({ ...draft, job_id: e.target.value || undefined })}
          />
        )}
      </label>
      <label className="block">
        <span className={labelCls}>Contract type {hint('contract_type')}</span>
        <select
          className={inputCls}
          value={draft.contract_type ?? ''}
          onChange={(e) =>
            onChange({
              ...draft,
              contract_type: (e.target.value || undefined) as SapEmploymentValues['contract_type'],
            })
          }
        >
          <option value="">Choose…</option>
          {SAP_CONTRACT_TYPES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </label>
      <label className="block">
        <span className={labelCls}>Weekly hours {hint('weekly_hours')}</span>
        <input
          className={inputCls}
          type="number"
          min={0}
          step={0.5}
          value={draft.weekly_hours ?? ''}
          onChange={(e) =>
            onChange({
              ...draft,
              weekly_hours: e.target.value === '' ? undefined : Number(e.target.value),
            })
          }
        />
      </label>
      <label className="block sm:col-span-2">
        <span className={labelCls}>Working time model {hint('working_time_model')}</span>
        <select
          className={inputCls}
          value={draft.working_time_model ?? ''}
          onChange={(e) => onChange({ ...draft, working_time_model: e.target.value || undefined })}
        >
          <option value="">Choose…</option>
          {(models.data?.entries ?? []).map((e) => (
            <option key={e.code} value={e.code}>
              {e.code} — {e.label}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}
